import { useEffect, useMemo, useState } from 'react';
import { Button } from '~/modules/Elements/AKUI/Button';
import Typography from '~/modules/Elements/AKUI/Primitives/Typography';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import { useIsHost, useMyPlayerId } from '~/modules/Multiplayer/hooks/useIsHost';
import { LeaderboardEntry, useMultiplayerScores } from '~/modules/Multiplayer/hooks/useMultiplayerScores';
import { playerColor } from '~/modules/Multiplayer/playerColors';
import { useRoom } from '~/modules/Multiplayer/hooks/useRoom';
import MultiplayerManager from '~/modules/Multiplayer/MultiplayerManager';
import ScoreText from '../../GameOverlay/Components/ScoreText';

interface Props {
  onNextSong: () => void;
  onBackToLobby: () => void;
  localScore: number;
}

function MultiplayerResults({ onNextSong, onBackToLobby, localScore: _localScore }: Props) {
  const scores = useMultiplayerScores();
  const isHost = useIsHost();
  const myId = useMyPlayerId();
  const room = useRoom();
  const songQueue = room?.songQueue ?? [];
  const { register } = useKeyboardNav();
  const [waitTimeout, setWaitTimeout] = useState(false);

  useEffect(() => {
    if (scores.length > 0) return;
    const id = setTimeout(() => setWaitTimeout(true), 15000);
    return () => clearTimeout(id);
  }, [scores.length]);

  const handleNextSong = () => {
    if (isHost) {
      MultiplayerManager.nextSong();
    }
    onNextSong();
  };

  const handleBackToLobby = () => {
    if (isHost) {
      MultiplayerManager.backToLobby();
    }
    onBackToLobby();
  };

  if (scores.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <Typography className="animate-pulse text-xl text-white/60">Czekam na wyniki...</Typography>
        {waitTimeout && (
          <Button onClick={onBackToLobby} size="small" className="bg-white/10 hover:bg-white/20">
            Wróć
          </Button>
        )}
      </div>
    );
  }

  // Until the server says SCORES, some players are still singing — their numbers are not final,
  // so no podium yet (it would reshuffle in front of everyone)
  const allFinished = room?.state !== 'PLAYING';
  const stillSinging = scores.filter((s) => !s.finished && s.connected);
  const top3 = allFinished ? scores.slice(0, 3) : [];
  const totalRank = new Map(
    [...scores].sort((a, b) => b.totalScore - a.totalScore).map((s, i) => [s.id, i + 1] as const),
  );
  const showTotals = scores.some((s) => s.totalScore !== s.score);

  return (
    <div className="relative flex flex-1 flex-col gap-4">
      {allFinished && <Confetti />}
      <Typography className="text-center text-3xl font-bold tracking-wider uppercase">Wyniki</Typography>

      {!allFinished && stillSinging.length > 0 && (
        <div className="animate-pulse rounded-lg bg-white/10 px-4 py-2 text-center text-white/80">
          Jeszcze śpiewa: {stillSinging.map((s) => s.name).join(', ')}
        </div>
      )}

      {/* Podium for top 3 */}
      {top3.length > 0 && (
        <div className="flex items-end justify-center gap-3">
          {top3.length >= 2 && <PodiumCard entry={top3[1]} rank={2} isMe={top3[1].id === myId} height="min-h-40" />}
          {top3.length >= 1 && <PodiumCard entry={top3[0]} rank={1} isMe={top3[0].id === myId} height="min-h-48" />}
          {top3.length >= 3 && <PodiumCard entry={top3[2]} rank={3} isMe={top3[2].id === myId} height="min-h-36" />}
        </div>
      )}

      {/* Everyone, with this song and the running total side by side */}
      <div data-test="mp-results-table" className="flex flex-col gap-1 rounded-lg bg-black/60 p-2">
        <div className="flex items-center gap-3 px-3 text-[11px] font-bold tracking-wider text-white/50 uppercase">
          <span className="w-8 text-center">#</span>
          <span className="flex-1">Gracz</span>
          <span className="w-24 text-right">Ta piosenka</span>
          {showTotals && <span className="w-28 text-right">Łącznie</span>}
        </div>
        {/* up to 16 players — the list scrolls instead of pushing the host's buttons off screen */}
        <div className="flex max-h-[36vh] flex-col gap-1 overflow-y-auto">
          {scores.map((entry, i) => {
            const isMe = entry.id === myId;
            return (
              <div
                key={entry.id}
                data-test="mp-results-row"
                data-name={entry.name}
                className={`flex items-center gap-3 rounded-lg px-3 py-1.5 ${isMe ? 'bg-white/15 ring-1 ring-white/30' : 'bg-white/5'} ${
                  entry.connected ? '' : 'opacity-50'
                }`}>
                <span className="w-8 text-center text-sm text-white/60">{i + 1}.</span>
                <Avatar entry={entry} />
                <span className={`min-w-0 flex-1 truncate ${isMe ? 'font-bold' : 'text-white/90'}`}>
                  {entry.name}
                  {isMe && <span className="font-normal text-white/60"> (Ty)</span>}
                  {!entry.connected && <span className="text-xs text-white/50"> · rozłączony</span>}
                  {entry.connected && !entry.finished && !allFinished && (
                    <span className="text-xs text-white/50"> · śpiewa…</span>
                  )}
                </span>
                <span className="w-24 text-right font-mono">
                  <ScoreText score={entry.score} />
                </span>
                {showTotals && (
                  <span className="w-28 text-right font-mono text-sm text-white/70">
                    <ScoreText score={entry.totalScore} />
                    <span className="text-xs text-white/40"> ({totalRank.get(entry.id)}.)</span>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Next song in queue preview */}
      {songQueue.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2">
          <span className="text-xs text-white/40">Następna:</span>
          <span className="truncate text-sm text-white/70">
            {songQueue[0].artist} - {songQueue[0].title}
          </span>
          {songQueue.length > 1 && (
            <span className="ml-auto text-xs whitespace-nowrap text-white/30">+{songQueue.length - 1} w kolejce</span>
          )}
        </div>
      )}

      <div className="mt-auto flex gap-4">
        {isHost ? (
          <>
            <Button
              {...register('next-song', handleNextSong, undefined, true)}
              size="small"
              className="flex-1 bg-green-700/60 text-xl hover:bg-green-600/70">
              {songQueue.length > 0 ? 'Następna piosenka' : 'Wybierz piosenkę'}
            </Button>
            <Button
              {...register('back-to-lobby', handleBackToLobby)}
              size="small"
              className="bg-white/10 text-lg hover:bg-white/20">
              Lobby
            </Button>
          </>
        ) : (
          <Typography className="w-full text-center text-lg text-white/60">Czekam na hosta...</Typography>
        )}
      </div>
    </div>
  );
}

function Avatar({ entry, size = 'h-7 w-7 text-xs' }: { entry: LeaderboardEntry; size?: string }) {
  return (
    <span
      className={`flex ${size} shrink-0 items-center justify-center rounded-full font-bold text-white`}
      style={{ backgroundColor: playerColor(entry.color).hex }}>
      {entry.name[0]?.toUpperCase()}
    </span>
  );
}

function PodiumCard({
  entry,
  rank,
  isMe,
  height,
}: {
  entry: LeaderboardEntry;
  rank: number;
  isMe: boolean;
  height: string;
}) {
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : '🥉';
  const bg =
    rank === 1
      ? 'bg-yellow-500/20 ring-yellow-500/40'
      : rank === 2
        ? 'bg-gray-300/15 ring-gray-300/30'
        : 'bg-amber-700/15 ring-amber-700/30';

  // min-h instead of a fixed height: with a fixed height the name (the only element allowed to shrink)
  // was squeezed to 0 px and the podium showed bare numbers
  return (
    <div
      data-test="mp-podium"
      data-name={entry.name}
      className={`flex w-40 flex-col items-center justify-end gap-1 rounded-xl ${bg} ${height} p-3 ring-1 ${
        isMe ? 'ring-2 ring-white/50' : ''
      }`}>
      <span className="text-2xl leading-none">{medal}</span>
      <Avatar entry={entry} size="h-8 w-8 text-sm" />
      <span className={`line-clamp-2 w-full text-center text-base break-words ${isMe ? 'font-bold' : ''}`}>
        {entry.name}
        {isMe && <span className="font-normal text-white/60"> (Ty)</span>}
      </span>
      <span className="font-mono text-lg font-bold">
        <ScoreText score={entry.score} />
      </span>
    </div>
  );
}

const CONFETTI_COLORS = ['#ffd700', '#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#ff9ff3', '#54a0ff', '#5f27cd'];

function Confetti() {
  const particles = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        size: 4 + Math.random() * 6,
        rotation: Math.random() * 360,
      })),
    [],
  );

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute animate-[confetti-fall_linear_forwards]"
          style={{
            left: `${p.left}%`,
            top: '-10px',
            width: `${p.size}px`,
            height: `${p.size * 0.6}px`,
            backgroundColor: p.color,
            borderRadius: '1px',
            transform: `rotate(${p.rotation}deg)`,
            animation: `confetti-fall ${p.duration}s ${p.delay}s linear forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(80vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default MultiplayerResults;
